// How the API's enum values read to a client, and the badge colour each gets. The portal words
// some statuses for the client ("Waiting on you" rather than "Sent").
import type { BadgeKind } from "@corecrm/ui";
import { humanize } from "@corecrm/ui";

const STATUS: Record<string, [string, BadgeKind]> = {
  // quotes
  sent: ["Waiting on you", "acc"],
  viewed: ["Waiting on you", "acc"],
  accepted: ["Accepted", "ok"],
  declined: ["Declined", "mute"],
  expired: ["Expired", "mute"],
  superseded: ["Replaced", "mute"],
  // projects
  planned: ["Planned", "info"],
  active: ["Active", "ok"],
  on_hold: ["On hold", "warn"],
  completed: ["Completed", "ok"],
  warranty: ["In warranty", "acc"], // UX-194
  closed: ["Closed", "mute"],
  cancelled: ["Cancelled", "mute"],
  // phases and tasks
  not_started: ["Not started", "mute"],
  in_progress: ["In progress", "info"],
  todo: ["To do", "mute"],
  done: ["Done", "ok"],
  // payment milestones
  pending: ["Upcoming", "mute"],
  due: ["Due", "warn"],
  invoiced: ["Invoiced", "info"],
  paid: ["Paid", "ok"],
  // deliverables
  submitted: ["Awaiting your approval", "acc"],
  changes_requested: ["Changes requested", "warn"],
  approved: ["Approved", "ok"],
  waived: ["Not needed", "mute"],
  // invoices
  open: ["Open", "warn"],
  void: ["Void", "mute"],
  uncollectible: ["Uncollectible", "bad"],
};

export function statusLabel(status: string): [string, BadgeKind] {
  return STATUS[status] ?? [humanize(status), "mute"];
}

export const BILLING_MODEL: Record<string, string> = {
  fixed_fee: "Fixed fee",
  time_and_materials: "Time & materials",
  maintenance: "Maintenance agreement",
  hosting: "Hosting agreement",
};

export const QUOTE_KIND: Record<string, string> = {
  standard: "Quote",
  change_request: "Change request",
  time_and_materials: "Time & materials quote",
  maintenance: "Maintenance agreement",
  hosting: "Hosting agreement",
};

export const TERM: Record<string, string> = {
  monthly: "Month to month",
  "1_year": "1 year",
  "2_years": "2 years",
  "3_years": "3 years",
};

export const PAYMENT_METHOD: Record<string, string> = {
  stripe: "Card",
  cheque: "Cheque",
  e_transfer: "e-Transfer",
  wire: "Wire",
  cash: "Cash",
  other: "Other",
};

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** "120" from the API's "120.00". */
export const quantity = (q: string) => String(Number(q));

/** What someone who isn't a billing contact sees where amounts would be (UX-192). */
export const BILLING_ONLY_NOTE =
  "Amounts, invoices and quotes are shown only to your account's billing contacts. Ask CoreNode to add you as one.";
